import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import api from '../../../../services/api';

import {
  Invoice,
  Route,
  RouteItem,
} from '../../../../Interfaces/RouteScripitingInterface';

import { color } from '../../../../utils/colors';
import { routeItemExample, seladData } from '../../../../utils/seladData';

import seladHorse from '../../../../assets/map/seladHorse.png';

import {
  AvailableRoutes,
  ButtonContainer,
  Container,
  RouteSequence,
  SaveRoutesButton,
  SequenceWrapper,
  SwitchContainer,
} from './_routeringDragAndDrop';

interface Props {
  availableInvoices: Invoice[];
  setAvailableInvoices: Function;
  savedRoutes?: Route[];
  setSavedRoutes: Function;
  setOriginRouteId: Function;
  setDestinationRouteId: Function;
  setTriggerRouteChanges: Function;
  triggerRouteChanges: boolean;
  showInvoiceInfo: string;
  setShowInvoiceInfo: Function;
  handleChanges: Function;
  loading: boolean;
  destinationRouteId?: string;
  setCurrentRouteDrawing: Function;
}

export const RouteringDragAndDrop = ({
  availableInvoices,
  setAvailableInvoices,
  savedRoutes,
  setSavedRoutes,
  setOriginRouteId,
  setDestinationRouteId,
  setTriggerRouteChanges,
  triggerRouteChanges,
  showInvoiceInfo,
  setShowInvoiceInfo,
  handleChanges,
  loading,
  destinationRouteId,
  setCurrentRouteDrawing,
}: Props) => {
  const routeItemModel: RouteItem = routeItemExample;

  const handleClick = (id: string) => {
    showInvoiceInfo !== '' ? setShowInvoiceInfo('') : setShowInvoiceInfo(id);
  };

  const handleReorderChange = async (id: string) => {
    try {
      await api.put(`/truck-routes/${id}/active-reorder`);
    } catch (error: any) {
      console.log(error?.response?.data?.message);
    }
  };

  const renderAvailablePoints = () => {
    return availableInvoices.map(({ id, identifier }, index) => {
      return (
        <Draggable draggableId={id} key={id} index={index}>
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.draggableProps}
              {...provided.dragHandleProps}
            >
              <div className="point" onClick={() => handleClick(id)}>
                {identifier}
              </div>
            </div>
          )}
        </Draggable>
      );
    });
  };

  const renderRouteSequences = () => {
    return savedRoutes?.map(({ id, route_items, reorder }, index) => {
      return (
        <SequenceWrapper className="sequence">
          <RouteSequence key={id} color={color(index)}>
            <Droppable droppableId={id} direction="horizontal">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {renderRoutePoints(route_items)}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </RouteSequence>
          <SwitchContainer color={color(index)}>
            <label className="switch__label">
              <span>Sequência bloqueada</span>
              <input
                className="switch__input"
                type="checkbox"
                defaultChecked={reorder}
                onChange={() => handleReorderChange(id)}
              />
            </label>

            <div
              className="switch__button"
              onClick={() => setCurrentRouteDrawing(id)}
            >
              Desenhar
            </div>
          </SwitchContainer>
        </SequenceWrapper>
      );
    });
  };

  const renderRoutePoints = (points: RouteItem[]) => {
    return points.map(({ invoice, identifier, id }, index, invoices) => {
      if (index === 0) {
        return (
          <div key={seladData.invoice.id + String(index)}>
            <div
              className="point starting-point"
              onClick={() => handleClick(seladData.invoice.id)}
            >
              <img src={seladHorse} alt="ícone Selad" />
            </div>
          </div>
        );
      }
      return (
        <Draggable draggableId={id} key={id} index={index}>
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.draggableProps}
              {...provided.dragHandleProps}
            >
              <div className="point" onClick={() => handleClick(invoice.id)}>
                {identifier}
              </div>
            </div>
          )}
        </Draggable>
      );
    });
  };

  const handleOnDragEnd = (result: any) => {
    const { source, destination } = result;

    if (!destination) {
      return;
    }

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    setOriginRouteId(source.droppableId);
    setDestinationRouteId(destination.droppableId);
    setTriggerRouteChanges(!triggerRouteChanges);

    if (source.droppableId === destination.droppableId) {
      source.droppableId === 'available-points'
        ? reorderAvailablePoints(result)
        : reorderRoutePoints(result);
    }

    if (source.droppableId !== destination.droppableId) {
      source.droppableId === 'available-points'
        ? moveFromAvailablePoints(result)
        : moveFromRoutePoints(result);
    }
  };

  const reorderAvailablePoints = (result: any) => {
    const { source, destination, draggableId } = result;

    const newOrder = Array.from(availableInvoices);
    const point = newOrder.find((point) => point.id === draggableId);

    newOrder.splice(source.index, 1);
    point && newOrder.splice(destination.index, 0, point);

    setAvailableInvoices(newOrder);
  };

  const reorderRoutePoints = (result: any) => {
    if (!savedRoutes) return;

    const { source, destination, draggableId } = result;

    const localRoutes = Array.from(savedRoutes);

    const choosenRoute = localRoutes.find(
      (route) => String(route.id) === source.droppableId,
    );

    const editingRoute = choosenRoute && Array.from(choosenRoute?.route_items);

    if (editingRoute) {
      const pointToMove = editingRoute.find(
        (point) => point.id === draggableId,
      );
      editingRoute.splice(source.index, 1);
      pointToMove && editingRoute.splice(destination.index, 0, pointToMove);

      setSavedRoutes(
        savedRoutes.map((item) =>
          item.id === source.droppableId
            ? { ...item, invoices: editingRoute }
            : item,
        ),
      );
    }
  };

  const moveFromAvailablePoints = (result: any) => {
    const { source, destination, draggableId } = result;
    let routeItem = routeItemModel;

    const sourceArray = Array.from(availableInvoices);
    const pointToMove = sourceArray.find((point) => point.id === draggableId);
    sourceArray.splice(source.index, 1);

    setAvailableInvoices(sourceArray);

    if (pointToMove) {
      routeItem.invoice = pointToMove;
      routeItem.id = pointToMove.id;
      routeItem.identifier = pointToMove.identifier;
      routeItem.latitude = pointToMove.latitude;
      routeItem.longitude = pointToMove.longitude;
    }

    if (pointToMove) {
      addPointToRoute(destination, routeItem);
    }
  };

  const moveFromRoutePoints = (result: any) => {
    if (!savedRoutes) return;
    const { source, destination, draggableId } = result;

    const sourceArray = Array.from(savedRoutes);
    const routeArray = sourceArray.find(
      (route) => route.id === source.droppableId,
    );

    const pointToMove = routeArray?.route_items.find(
      (point) => point.id === draggableId,
    );

    routeArray?.route_items.splice(source.index, 1);

    if (pointToMove) {
      destination.droppableId === 'available-points'
        ? addPointToAvailablePoint(destination, pointToMove.invoice)
        : addPointToRoute(destination, pointToMove);
    }
  };

  const addPointToRoute = async (destination: any, pointToMove: RouteItem) => {
    if (!savedRoutes) return;

    const routesCollection = Array.from(savedRoutes);

    const destinationArray = routesCollection.find(
      (route) => route.id === destination.droppableId,
    );

    if (pointToMove && destinationArray) {
      let reorderArray = Array.from(destinationArray.route_items);

      if (reorderArray.length > 1) {
        reorderArray.splice(destination.index, 0, pointToMove);
      }

      if (reorderArray.length === 1) {
        reorderArray = [...reorderArray, pointToMove];
      }

      setSavedRoutes(
        savedRoutes.map((item) =>
          item.id === destination.droppableId
            ? { ...item, route_items: reorderArray }
            : item,
        ),
      );
    }
  };

  const addPointToAvailablePoint = (destination: any, pointToMove: Invoice) => {
    const availableCollection = Array.from(availableInvoices);

    availableCollection.splice(destination.index, 0, pointToMove);

    setAvailableInvoices(availableCollection);
  };

  const disabled = () => {
    if (!savedRoutes || loading || !destinationRouteId) return true;

    const hasInformation = !savedRoutes.every(({ truck_id }) => {
      return truck_id;
    });

    return hasInformation;
  };

  return (
    <Container className="routering-dragger">
      <DragDropContext onDragEnd={handleOnDragEnd}>
        <AvailableRoutes>
          <Droppable droppableId="available-points" direction="horizontal">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {renderAvailablePoints()}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </AvailableRoutes>

        {renderRouteSequences()}
      </DragDropContext>

      <ButtonContainer>
        <SaveRoutesButton disabled={disabled()} onClick={() => handleChanges()}>
          <span>Salvar alterações</span>
        </SaveRoutesButton>
      </ButtonContainer>
    </Container>
  );
};
